import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { toast, Toaster } from "sonner";
import Logo from "../components/Logo";
import { useConfirm } from "../components/Confirm";
import CommandDeck from "./modules/CommandDeck";
import Generic from "./modules/Generic";
import ReleasePlanning from "./modules/ReleasePlanning";
import TeamAccess from "./modules/TeamAccess";
import RecentlyDeleted from "./modules/RecentlyDeleted";
import RoyaltySplits from "./modules/RoyaltySplits";
import Finance from "./modules/Finance";
import EmailSettings from "./modules/EmailSettings";
import ChangeRequests from "./modules/ChangeRequests";
import {
  LayoutGrid, Search, Inbox, Music4, CalendarClock, Disc3, Users, Percent,
  Megaphone, Film, TrendingUp, MessageSquare, Wallet, Scale, ShieldCheck,
  Trash2, LogOut, Mail, ChevronDown, Radio, Menu, X, RefreshCw, Sun, Moon, Bot
} from "lucide-react";
import { useTheme, triggerGlobalRefresh } from "../hooks/useAppShell";
import NotificationsBell from "../components/NotificationsBell";

const NAV = [
  { key: "overview",   label: "Command Deck",      icon: LayoutGrid,    countKey: null,             path: "" },
  { key: "scouting",   label: "Scouting",           icon: Search,        countKey: "scouting",       path: "scouting" },
  { key: "demo_submissions", label: "Demo Submissions", icon: Inbox,     countKey: "demo_submissions", path: "demo-submissions" },
  { key: "song_plan",  label: "Song Plan",          icon: Music4,        countKey: null,             group: true,
    children: [
      { key: "release_planning", label: "Release Planning", icon: CalendarClock, countKey: "release_planning", path: "release-planning" },
      { key: "content_strategy", label: "Content Strategy", icon: Film,          countKey: "content_strategy", path: "content" },
    ]
  },
  { key: "released_songs", label: "Released Songs", icon: Disc3,         countKey: "released_songs", path: "released-songs" },
  { key: "artist_database", label: "Artist Database", icon: Users,       countKey: "artist_database", path: "artists" },
  { key: "royalty_splits", label: "Royalty Splits", icon: Percent,       countKey: "royalty_splits", path: "royalty",   adminOnly: true },
  { key: "legal_tracker",  label: "Legal Tracker",  icon: Scale,         countKey: "legal_tracker",  path: "legal",     adminOnly: true },
  { key: "marketing",      label: "Marketing",      icon: Megaphone,     countKey: "marketing",      path: "marketing" },
  { key: "future_strategy", label: "Future Strategy", icon: TrendingUp,  countKey: "future_strategy", path: "future" },
  { key: "feedback",       label: "Feedback",       icon: MessageSquare, countKey: "feedback",       path: "feedback" },
  { key: "finance",        label: "Finance",        icon: Wallet,        countKey: "finance",        path: "finance",   adminOnly: true },
  { key: "team",           label: "Team Access",    icon: ShieldCheck,   countKey: "team",           path: "team",      adminOnly: true },
  { key: "email",          label: "Email & Digest", icon: Mail,          countKey: null,             path: "email",     adminOnly: true },
  { key: "deleted",        label: "Recently Deleted", icon: Trash2,      countKey: "recently_deleted", path: "deleted", adminOnly: true },
  { key: "change_requests", label: "Change Requests", icon: Bot,          countKey: "pending_change_requests", path: "change-requests", adminOnly: true },
];

export default function Dashboard({ user }) {
  const loc = useLocation();
  const currentPath = loc.pathname.replace(/^\/dashboard\/?/, "");
  const [counts, setCounts] = useState({});
  const [now, setNow] = useState(new Date());
  const [songPlanOpen, setSongPlanOpen] = useState(() => currentPath.startsWith("release-planning") || currentPath.startsWith("content"));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();
  const confirm = useConfirm();

  const handleRefresh = () => {
    setRefreshing(true);
    // Bump counts + broadcast a global refetch. Every module reloads its data.
    api.counts().then(setCounts).catch((err) => console.error("counts refresh failed:", err));
    triggerGlobalRefresh();
    toast.success("Refreshed");
    setTimeout(() => setRefreshing(false), 700);
  };

  useEffect(() => {
    const load = () => api.counts().then(setCounts).catch((err) => console.error("Failed to load counts:", err));
    load();
    const iv = setInterval(load, 15000); // refresh every 15s
    window.addEventListener("ye:counts-changed", load);
    return () => { clearInterval(iv); window.removeEventListener("ye:counts-changed", load); };
  }, []);
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => {
    if (currentPath.startsWith("release-planning") || currentPath.startsWith("content")) setSongPlanOpen(true);
  }, [currentPath]);
  // Close mobile drawer whenever route changes
  useEffect(() => { setSidebarOpen(false); }, [loc.pathname]);
  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (sidebarOpen) document.body.classList.add("no-scroll");
    else document.body.classList.remove("no-scroll");
    return () => document.body.classList.remove("no-scroll");
  }, [sidebarOpen]);

  const logout = () => {
    confirm(
      `You'll be signed out and returned to the login screen. Continue?`,
      async () => {
        try { await api.logout(); } catch {}
        window.location.replace("/login");
      },
      { title: "Sign out?", confirmLabel: "Yes, sign out", cancelLabel: "Stay signed in", tone: "cyan" }
    );
  };

  const items = NAV.filter(n => !n.adminOnly || user.role === "admin");
  const timeStr = now.toLocaleTimeString("en-GB", { hour12: false });

  const renderNavItem = (n, indent = false) => {
    const active = (n.path === "" && (currentPath === "" || currentPath === "/")) || (n.path && currentPath.startsWith(n.path));
    const Icon = n.icon;
    const count = n.countKey ? counts[n.countKey] : null;
    return (
      <Link key={n.key} to={`/dashboard/${n.path}`} className={`sidebar-item ${active ? "active" : ""}`} data-testid={`nav-${n.key}`}
            style={{ paddingLeft: indent ? 30 : 12 }}>
        <Icon size={15} />
        <span style={{ flex: 1 }}>{n.label}</span>
        {count != null && count > 0 && (
          <span className="badge" data-testid={`count-${n.key}`} style={{ padding: "2px 7px", fontSize: 10, background: active ? "rgba(76,232,206,0.18)" : "var(--panel2)", color: active ? "var(--mint)" : "var(--muted)", borderColor: active ? "rgba(76,232,206,0.4)" : "var(--border)" }}>
            {count}
          </span>
        )}
      </Link>
    );
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#101513", border: "1px solid #1f2724", color: "#e8efec" } }} />
      {/* Mobile overlay */}
      <div
        className={`mobile-sidebar-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
        data-testid="mobile-sidebar-overlay"
      />
      <div className="grain app-shell">
        <aside
          className={`app-sidebar ${sidebarOpen ? "open" : ""}`}
          data-testid="app-sidebar"
          style={{
            borderRight: "1px solid var(--border)",
            padding: "22px 16px",
            background: "linear-gradient(180deg, rgba(124,108,255,0.045) 0%, rgba(0,0,0,0) 60%), var(--panel)",
            position: "sticky", top: 0, height: "100vh", overflowY: "auto",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Mobile: close button */}
          <button
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
            aria-label="Close menu"
            style={{
              display: "none",
              position: "absolute", top: 12, right: 12,
              background: "transparent", border: "1px solid var(--border)",
              borderRadius: 999, padding: 6, cursor: "pointer", color: "var(--text-dim)",
            }}
            className="mobile-close-btn"
          >
            <X size={16} />
          </button>
          {/* Brand */}
          <div style={{ padding: "0 4px 18px 4px", borderBottom: "1px solid var(--border)", marginBottom: 16 }}>
            <Logo size={64} />
            <div className="muted" style={{ fontSize: 10, letterSpacing: "0.22em", marginTop: 8 }} data-testid="tagline">OPERATIONS OS</div>
            <div className="muted" style={{ fontSize: 10, letterSpacing: "0.18em", opacity: 0.6, marginTop: 2 }} data-testid="version">v1.6</div>
          </div>

          {/* Profile */}
          <div style={{ padding: "0 4px 16px 4px", borderBottom: "1px solid var(--border)", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }} data-testid="current-user-name">{user.name}</div>
              <div className="muted" style={{ fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid="current-user-email">{user.email}</div>
              <span className="badge badge-brand" data-testid="current-user-role" style={{ padding: "3px 10px", fontSize: 10, marginTop: 6, display: "inline-block", textTransform: "uppercase", letterSpacing: "0.08em" }}>{user.role}</span>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="btn" title="Sign out" style={{ padding: "6px 8px" }}>
              <LogOut size={13} />
            </button>
          </div>

          {/* Nav */}
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {items.map(n => {
              if (n.group) {
                const anyActive = n.children.some(c => currentPath.startsWith(c.path));
                const Icon = n.icon;
                const totalCount = n.children.reduce((s, c) => s + (counts[c.countKey] || 0), 0);
                return (
                  <div key={n.key}>
                    <button
                      onClick={() => setSongPlanOpen(o => !o)}
                      className={`sidebar-item clickable ${anyActive ? "active" : ""}`}
                      data-testid={`nav-${n.key}`}
                      style={{ width: "100%", background: "transparent", border: anyActive ? "1px solid rgba(76,232,206,0.28)" : "none", cursor: "pointer", textAlign: "left" }}
                    >
                      <Icon size={15} />
                      <span style={{ flex: 1 }}>{n.label}</span>
                      {totalCount > 0 && (
                        <span className="badge" data-testid={`count-${n.key}`} style={{ padding: "2px 7px", fontSize: 10, background: "var(--panel2)", color: "var(--muted)", marginRight: 4 }}>{totalCount}</span>
                      )}
                      <ChevronDown size={13} style={{ transition: "transform 180ms ease", transform: songPlanOpen ? "rotate(0deg)" : "rotate(-90deg)" }} />
                    </button>
                    {songPlanOpen && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 3 }}>
                        {n.children.map(c => renderNavItem(c, true))}
                      </div>
                    )}
                  </div>
                );
              }
              return renderNavItem(n);
            })}
          </div>
        </aside>

        <main className="app-main" style={{ padding: "0", overflowX: "hidden", minWidth: 0 }}>
          {/* Mobile top bar with hamburger */}
          <div className="mobile-topbar" data-testid="mobile-topbar">
            <button
              onClick={() => setSidebarOpen(true)}
              data-testid="open-sidebar-btn"
              aria-label="Open menu"
              style={{
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: 10,
                padding: "8px 10px",
                cursor: "pointer",
                color: "var(--text)",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Menu size={18} />
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Logo size={28} />
              <span style={{ fontFamily: "Space Grotesk", fontWeight: 700, letterSpacing: "0.06em", fontSize: 13 }}>YE MUSIC</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <NotificationsBell variant="mobile" />
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle-mobile"
                aria-label="Toggle theme"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: 999, padding: 8, cursor: "pointer", color: "var(--text-dim)", display: "inline-flex" }}
              >
                {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                onClick={handleRefresh}
                data-testid="refresh-btn-mobile"
                aria-label="Refresh data"
                title="Refresh all data"
                style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: 999, padding: 8, cursor: "pointer", color: "var(--text-dim)", display: "inline-flex" }}
              >
                <RefreshCw size={14} className={refreshing ? "spin" : ""} />
              </button>
            </div>
          </div>

          {/* Desktop top status bar */}
          <div className="status-bar" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, padding: "16px 36px 0 36px", position: "relative", zIndex: 50 }}>
            <span className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
              <Radio size={11} style={{ color: "var(--emerald)" }} /> SYSTEM ONLINE
            </span>
            <span style={{ fontSize: 12, fontFamily: "Space Grotesk", fontWeight: 700,
              background: "linear-gradient(135deg, #a78bfa, #22d3ee)",
              WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}
              data-testid="system-clock">{timeStr}</span>
            <NotificationsBell variant="desktop" />
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label="Toggle theme"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="btn"
              style={{ padding: "6px 10px", borderRadius: 999 }}
            >
              {theme === "dark" ? <Sun size={13} /> : <Moon size={13} />}
            </button>
            <button
              onClick={handleRefresh}
              data-testid="refresh-btn"
              className="btn"
              title="Refresh all data — pulls the latest entries in every module"
              style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <RefreshCw size={12} className={refreshing ? "spin" : ""} />
              Refresh
            </button>
          </div>

          <div className="page-padding" style={{ padding: "12px 36px 28px 36px" }}>
            <Routes>
              <Route path="" element={<CommandDeck user={user} />} />
              <Route path="scouting" element={<Generic user={user} dept="scouting" title="Scouting" fields={SCOUTING_FIELDS} statusOptions={["Watching","Contacted","In Talks","Signed","Passed"]} />} />
              <Route path="demo-submissions" element={<Generic user={user} dept="demo_submissions" title="Demo Submissions" fields={DEMO_FIELDS} statusOptions={["Shortlisted","Approved","Discard"]} />} />
              <Route path="release-planning" element={<ReleasePlanning user={user} />} />
              <Route path="released-songs" element={<Generic user={user} dept="released_songs" title="Released Songs" fields={RELEASED_FIELDS} />} />
              <Route path="artists" element={<Generic user={user} dept="artist_database" title="Artist Database" fields={ARTIST_FIELDS} />} />
              <Route path="royalty" element={<RoyaltySplits user={user} />} />
              <Route path="marketing" element={<Generic user={user} dept="marketing" title="Marketing" fields={MARKETING_FIELDS} />} />
              <Route path="content" element={<Generic user={user} dept="content_strategy" title="Content Strategy" fields={CONTENT_FIELDS} statusOptions={["Idea","Scripting","Ready","Posted","Completed"]} sheetEmbedField="asset_calendar" />} />
              <Route path="future" element={<Generic user={user} dept="future_strategy" title="Future Strategy" fields={FUTURE_FIELDS} statusOptions={["Proposed","Under Review","Approved","Archived"]} />} />
              <Route path="feedback" element={<Generic user={user} dept="feedback" title="Feedback" fields={FEEDBACK_FIELDS} />} />
              <Route path="finance" element={<Finance user={user} />} />
              <Route path="legal" element={<Generic user={user} dept="legal_tracker" title="Legal Tracker" fields={LEGAL_FIELDS} statusOptions={["Pending","Signed","Expired"]} />} />
              <Route path="team" element={<TeamAccess user={user} />} />
              <Route path="email" element={<EmailSettings />} />
              <Route path="deleted" element={<RecentlyDeleted user={user} />} />
              <Route path="change-requests" element={<ChangeRequests user={user} />} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
}

const SCOUTING_FIELDS = [
  { key: "artist", label: "Artist / Act", type: "text", required: true },
  { key: "platform", label: "Platform", type: "select", options: ["Instagram", "YouTube", "Spotify", "SoundCloud", "Referral"] },
  { key: "genre", label: "Genre", type: "text" },
  { key: "date", label: "Date", type: "date" },
  { key: "instagram", label: "Instagram", type: "url" },
  { key: "spotify", label: "Spotify", type: "url" },
  { key: "youtube", label: "YouTube", type: "url" },
];
const DEMO_FIELDS = [
  { key: "song", label: "Song Name", type: "text", required: true },
  { key: "producer", label: "Producer Name", type: "text" },
  { key: "producer_email", label: "Email", type: "text" },
  { key: "date", label: "Date", type: "date" },
  { key: "soundcloud", label: "Audio Link", type: "url" },
  { key: "genre", label: "Genre", type: "text" },
  { key: "notes", label: "Notes from Submitter", type: "textarea" },
  { key: "source", label: "Source", type: "text" },
];
const RELEASED_FIELDS = [
  { key: "title", label: "Song Name", type: "text", required: true },
  { key: "artists", label: "Artist(s)", type: "multiArtistSelect", required: true },
  { key: "lyricists", label: "Lyricist(s)", type: "multiArtistSelect" },
  { key: "mix_engineers", label: "Mix Engineer(s)", type: "multiArtistSelect" },
  { key: "release_date", label: "Release Date", type: "date" },
  { key: "spotify", label: "Spotify", type: "url" },
  { key: "apple_music", label: "Apple Music", type: "url" },
  { key: "youtube_music", label: "YouTube Music", type: "url" },
  { key: "youtube_video", label: "YouTube Video", type: "url" },
];
const ARTIST_ROLES = ["Vocalist","Producer","Lyricist","Mix Engineer","Master Engineer","Guitarist","Bassist","Drummer","Keyboardist","Songwriter","Composer","Arranger","DJ","Rapper","Singer-Songwriter","Instrumentalist","Session Musician","Backing Vocalist","Cover Artist","Video Director","Choreographer","Photographer","Manager","A&R","Publisher","Sound Designer","Foley Artist","Voice Actor","Other"];
const ARTIST_FIELDS = [
  { key: "name", label: "Artist Name", type: "text", required: true },
  { key: "role", label: "Role", type: "select", options: ARTIST_ROLES },
  { key: "spotify", label: "Spotify", type: "url" },
  { key: "apple_music", label: "Apple Music", type: "url" },
  { key: "instagram", label: "Instagram", type: "url" },
  { key: "show_on_pitch", label: "Show on Public Pitch", type: "boolean", hint: "Turn ON for artists you want to showcase on /pitch. Only songs credited to a toggled artist appear on the public pitch — guest features on non-toggled artists stay hidden." },
];
const MARKETING_FIELDS = [
  { key: "campaign", label: "Campaign", type: "text", required: true },
  { key: "linked_release", label: "Linked Release", type: "releaseSelect" },
  { key: "channel", label: "Channel", type: "select", options: ["Meta Ads", "Google Ads", "Spotify Playlisting", "Organic"] },
  { key: "date", label: "Date", type: "date" },
  { key: "budget", label: "Budget (₹)", type: "number" },
];
const CONTENT_FIELDS = [
  { key: "title", label: "Title / Idea", type: "text", required: true },
  { key: "linked_release", label: "Linked Release", type: "releaseSelect" },
  { key: "date", label: "Date", type: "date" },
  { key: "ar_name", label: "A&R Name (auto)", type: "text", locked: true },
  { key: "asset_calendar", label: "Asset Link (Google Sheet or Canva Design)", type: "url" },
];
const FUTURE_CATEGORIES = ["Catalog & Signings","Artist Development","Distribution & DSP Strategy","Publishing & Sync","International Expansion","Partnerships","Revenue Diversification","Risk & Compliance","Marketing & Brand"];
const FUTURE_FIELDS = [
  { key: "title", label: "Strategy Title", type: "text", required: true },
  { key: "category", label: "Category", type: "select", options: FUTURE_CATEGORIES },
  { key: "priority", label: "Priority", type: "select", options: ["High","Medium","Low"] },
  { key: "date", label: "Date", type: "date" },
  { key: "investment", label: "Investment Required (₹)", type: "number" },
  { key: "notes", label: "Notes", type: "textarea" },
];
const FEEDBACK_FIELDS = [
  { key: "submitted_by", label: "Submitted By (auto)", type: "text", locked: true },
  { key: "feedback", label: "Feedback", type: "textarea", required: true },
];
const LEGAL_DOC_TYPES = ["NOC", "Artist/Producer/Lyricist Agreement", "Song Deal / Master License", "Publishing Agreement", "Sample Clearance", "Sync License", "Other"];
const LEGAL_FIELDS = [
  { key: "document_type", label: "Document Type", type: "select", options: LEGAL_DOC_TYPES, required: true },
  { key: "linked_artist_id", label: "Linked Artist/Producer/Lyricist", type: "artistSelect" },
  { key: "linked_song_id", label: "Linked Song", type: "releaseSelect" },
  { key: "date", label: "Date", type: "date" },
  { key: "drive_link", label: "Drive Link", type: "url" },
  { key: "notes", label: "Notes", type: "textarea" },
];
